import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { UnifiedMetric } from '@/types/unifiedLibrary';
import { dataSyncEvents } from '@/utils/dataSyncEvents';
import { useAuth } from '@/components/Auth/AuthProvider';
import { CurrencySelect } from '@/components/CurrencySelect';
import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';
import { Calendar, DollarSign, Hash, FileText, Target, Settings, Database, Copy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EnhancedMetricModalProps {
  metric: UnifiedMetric | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EnhancedMetricModal = ({ metric, isOpen, onClose, onSuccess }: EnhancedMetricModalProps) => {
  console.log('EnhancedMetricModal render:', { metric: metric?.name, isOpen });
  
  const [formData, setFormData] = useState({
    customValue: '',
    customDescription: '',
    customUnit: '',
    customCurrency: 'USD',
    customMetadata: '',
    isCustom: false,
    reportingPeriod: new Date().toISOString().split('T')[0],
    dataSource: 'Library Import',
    scope: '',
    validationStatus: 'pending'
  });
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Pre-populate form data when metric changes
  useEffect(() => {
    if (metric) {
      setFormData({
        customValue: '',
        customDescription: metric.description || '',
        customUnit: metric.unit || '',
        customCurrency: metric.currency || 'USD',
        customMetadata: '',
        isCustom: false,
        reportingPeriod: new Date().toISOString().split('T')[0],
        dataSource: 'Library Import',
        scope: metric.scope || '',
        validationStatus: 'pending'
      });
      setSelectedTags([]);
    }
  }, [metric]);

  // Reset form when modal opens with new metric
  React.useEffect(() => {
    if (metric && isOpen) {
      setFormData({
        customValue: '',
        customDescription: metric.description || '',
        customUnit: metric.unit,
        customCurrency: metric.currency || 'USD',
        customMetadata: '',
        isCustom: false
      });
      setSelectedTags([]);
    }
  }, [metric, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!metric || !user) return;

    setLoading(true);
    try {
      // Parse custom metadata
      let customMetadata = {};
      if (formData.customMetadata) {
        try {
          customMetadata = JSON.parse(formData.customMetadata);
        } catch (error) {
          customMetadata = { notes: formData.customMetadata };
        }
      }

      console.log('Saving metric to Supabase:', {
        userId: user.id,
        metricData: {
          organization_id: user.id,
          metric_id: metric.id,
          name: metric.name,
          category: metric.category,
          subcategory: metric.subcategory,
          description: metric.description || '',
          unit: formData.customUnit || metric.unit,
          currency: formData.customCurrency,
          custom_value: formData.customValue ? parseFloat(formData.customValue) : 0,
          custom_metadata: {
            ...customMetadata,
            custom_description: formData.customDescription,
            custom_unit: formData.customUnit,
            custom_currency: formData.customCurrency,
            is_custom: formData.isCustom,
            reporting_period: formData.reportingPeriod,
            data_source: formData.dataSource,
            scope: formData.scope,
            validation_status: formData.validationStatus
          },
          is_custom: false
        }
      });

      // Create user metric in Supabase
      const { data: createdMetric, error } = await supabase
        .from('user_metrics_data')
        .insert({
          organization_id: user.id,
          metric_id: metric.id,
          name: metric.name,
          category: metric.category,
          subcategory: metric.subcategory,
          description: metric.description || '',
          unit: formData.customUnit || metric.unit,
          currency: formData.customCurrency,
          custom_value: formData.customValue ? parseFloat(formData.customValue) : 0,
          custom_metadata: {
            ...customMetadata,
            custom_description: formData.customDescription,
            custom_unit: formData.customUnit,
            custom_currency: formData.customCurrency,
            is_custom: formData.isCustom,
            reporting_period: formData.reportingPeriod,
            data_source: formData.dataSource,
            scope: formData.scope,
            validation_status: formData.validationStatus
          },
          is_custom: false
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      console.log('Save result:', createdMetric);

      // Apply tags to the created metric if any are selected
      if (selectedTags.length > 0 && createdMetric) {
        try {
          // Import tagService dynamically to avoid circular dependencies
          const { tagService } = await import('@/services/tagService');
          await tagService.tagData(user.id, 'metric', createdMetric.id, selectedTags);
        } catch (tagError) {
          console.warn('Failed to apply tags to metric:', tagError);
          // Don't fail the entire operation if tagging fails
        }
      }

      // Emit event to notify other components
      if (createdMetric) {
        dataSyncEvents.emitMetricChange('created', createdMetric.id, user.id);
      }

      toast({
        title: "Metric Added Successfully",
        description: `${metric.name} has been added to your organization data.${selectedTags.length > 0 ? ` ${selectedTags.length} tag(s) applied.` : ''}`
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding metric:', error);
      toast({
        title: "Error",
        description: "Failed to add metric. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!metric) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Add Metric to Organization: {metric.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Original Metric Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Copy className="h-5 w-5" />
                Original Metric Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Name</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border font-mono text-sm">
                    {metric.name}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <Badge variant="outline">{metric.category}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Subcategory</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {metric.subcategory}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Unit</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {metric.unit}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Difficulty</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <Badge variant="outline">{metric.difficulty || 'Not specified'}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Mandatory</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {metric.mandatory ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              
              {metric.description && (
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                    {metric.description}
                  </div>
                </div>
              )}

              {metric.scope && (
                <div>
                  <Label className="text-sm font-medium">Scope</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <Badge variant="outline">{metric.scope}</Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customization Options */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="h-5 w-5" />
                Customize for Your Organization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isCustom"
                  checked={formData.isCustom}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isCustom: checked }))}
                />
                <Label htmlFor="isCustom">Mark as custom metric</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customValue">Custom Value</Label>
                  <Input
                    id="customValue"
                    type="number"
                    step="0.01"
                    placeholder="Enter your organization's value"
                    value={formData.customValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, customValue: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customUnit">Custom Unit</Label>
                  <Input
                    id="customUnit"
                    placeholder="Custom unit (optional)"
                    value={formData.customUnit}
                    onChange={(e) => setFormData(prev => ({ ...prev, customUnit: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customCurrency">Currency</Label>
                  <CurrencySelect
                    value={formData.customCurrency}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, customCurrency: value }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.customDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, customDescription: e.target.value }))}
                  placeholder="Additional notes or context about this metric"
                  rows={3}
                />
              </div>

              {/* Tags Section */}
              <div>
                <Label className="text-sm font-medium">Tags</Label>
                <p className="text-xs text-gray-600 mb-2">
                  Add tags to organize and categorize this metric for better analysis and filtering
                </p>
                <DataTaggingComponent
                  selectedTags={selectedTags}
                  onTagsChange={setSelectedTags}
                  dataType="metric"
                  showCreateTag={true}
                />
              </div>

              {/* Additional Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reportingPeriod">Reporting Period</Label>
                  <Input
                    id="reportingPeriod"
                    type="date"
                    value={formData.reportingPeriod}
                    onChange={(e) => setFormData(prev => ({ ...prev, reportingPeriod: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="dataSource">Data Source</Label>
                  <Input
                    id="dataSource"
                    placeholder="e.g., Internal Systems, Supplier Data, etc."
                    value={formData.dataSource}
                    onChange={(e) => setFormData(prev => ({ ...prev, dataSource: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="scope">Scope</Label>
                  <Select value={formData.scope} onValueChange={(value) => setFormData(prev => ({ ...prev, scope: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Scope 1">Scope 1</SelectItem>
                      <SelectItem value="Scope 2">Scope 2</SelectItem>
                      <SelectItem value="Scope 3">Scope 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="validationStatus">Validation Status</Label>
                  <Select value={formData.validationStatus} onValueChange={(value) => setFormData(prev => ({ ...prev, validationStatus: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="validated">Validated</SelectItem>
                      <SelectItem value="review_required">Review Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Standards and Factors Information */}
          {(metric.standards && Object.keys(metric.standards).length > 0) || 
           (metric.factors && Object.keys(metric.factors).length > 0) ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5" />
                  Standards & Factors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {metric.standards && Object.keys(metric.standards).length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Standards Mapping</Label>
                    <div className="mt-2 space-y-3">
                      {Object.entries(metric.standards).map(([standard, codes]) => (
                        <div key={standard} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                              {standard}
                            </Badge>
                            <span className="text-sm font-medium text-blue-900">
                              {standard === 'GRI' ? 'Global Reporting Initiative' :
                               standard === 'SASB' ? 'Sustainability Accounting Standards Board' :
                               standard === 'SDG' ? 'Sustainable Development Goals' :
                               standard === 'TCFD' ? 'Task Force on Climate-related Financial Disclosures' :
                               standard === 'CDP' ? 'Carbon Disclosure Project' :
                               standard === 'IFRS_S1' ? 'IFRS S1 - General Requirements' :
                               standard === 'IFRS_S2' ? 'IFRS S2 - Climate-related Disclosures' :
                               standard === 'BRSR' ? 'Business Responsibility and Sustainability Report' :
                               standard}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(codes) ? codes.map((code, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {code}
                              </Badge>
                            )) : (
                              <Badge variant="secondary" className="text-xs">
                                {String(codes)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {metric.factors && Object.keys(metric.factors).length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Conversion Factors</Label>
                    <div className="mt-2 space-y-3">
                      {Object.entries(metric.factors).map(([factorType, factorData]) => (
                        <div key={factorType} className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                              {factorType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {typeof factorData === 'object' && factorData !== null ? (
                              Object.entries(factorData).map(([key, value]) => (
                                <div key={key} className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-green-900">
                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                  </span>
                                  <span className="text-green-700">
                                    {Array.isArray(value) ? (
                                      <div className="flex flex-wrap gap-1">
                                        {value.map((item, index) => (
                                          <Badge key={index} variant="outline" className="text-xs bg-white">
                                            {item}
                                          </Badge>
                                        ))}
                                      </div>
                                    ) : typeof value === 'number' ? (
                                      <Badge variant="outline" className="text-xs bg-white">
                                        {value}
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs bg-white">
                                        {String(value)}
                                      </Badge>
                                    )}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="flex justify-between items-center text-sm">
                                <span className="font-medium text-green-900">Value:</span>
                                <Badge variant="outline" className="text-xs bg-white">
                                  {String(factorData)}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add to Organization'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};